import { API_BASE } from './config';

const TOKEN_KEY = 'mh_session_token';
const DEFAULT_TIMEOUT_MS = 30000;
const RETRYABLE = new Set([408, 429, 500, 502, 503, 504]);

function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || '';
  } catch (err) {
    return '';
  }
}

function setToken(value) {
  try {
    if (value) window.localStorage.setItem(TOKEN_KEY, value);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch (err) {
    /* storage indisponível */
  }
}

function apiError(message, status) {
  const error = new Error(message);
  error.status = status;
  return error;
}

let unauthorizedHandler = null;

export function setUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

async function parseBody(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (err) {
    return null;
  }
}

async function requestOnce(path, { body, timeout = DEFAULT_TIMEOUT_MS, ...rest } = {}) {
  const token = getToken();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeout);
  let res;
  try {
    res = await fetch(`${API_BASE}/api${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      ...rest,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw apiError('Tempo limite da requisição esgotado. Tente novamente.', 0);
    }
    throw apiError('API offline (backend não está rodando)', 0);
  } finally {
    clearTimeout(timer);
  }

  const data = await parseBody(res);

  if (res.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
    throw apiError((data && data.error) || 'Token de acesso inválido ou ausente', 401);
  }
  if (!res.ok) {
    const message = (data && data.error) || `Falha HTTP ${res.status}`;
    const error = apiError(message, res.status);
    error.retryable = RETRYABLE.has(res.status);
    throw error;
  }
  return data;
}

async function request(path, options = {}) {
  const { retry = true, ...rest } = options;
  const isRead = !rest.method || rest.method === 'GET' || rest.method === 'HEAD';
  const attempts = retry && isRead ? 2 : 1;

  let lastError = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await requestOnce(path, rest);
    } catch (err) {
      lastError = err;
      const canRetry = attempt < attempts - 1 && err && (err.status === 0 || err.retryable);
      if (!canRetry) throw err;
      await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
    }
  }
  throw lastError;
}

export const api = {
  get: (path, options = {}) => request(path, options),
  post: (path, body = {}, options = {}) => request(path, { method: 'POST', body, ...options }),
  put: (path, body = {}, options = {}) => request(path, { method: 'PUT', body, ...options }),
  del: (path, options = {}) => request(path, { method: 'DELETE', ...options }),
};

export { getToken, setToken };
